import { GET_ORGANIZATIONS_OPTIONS } from "@/store/organizations.module";
import { GET_STOCK_WAREHOUSE_OPTIONS } from "@/store/stock/stockWarehouses.module";
import { SHOW_SNACK } from "@/store/snack.module";
import validations from "@/views/partials/form/Validations.vue";

/**
 * Applies global stock context (organization / warehouse bar) to document create/edit dropdowns.
 */
export default {
  data() {
    return {
      validations,
    };
  },
  computed: {
    stockContextOrganizationIds() {
      return this.$store.getters.stockContextOrganizationIds || [];
    },
    stockContextWarehouseIds() {
      return this.$store.getters.stockContextWarehouseIds || [];
    },
    stockContextVersion() {
      return this.$store.getters.stockContextVersion;
    },
  },
  watch: {
    stockContextVersion() {
      this.handleStockContextChange();
    },
  },
  methods: {
    clearHeaderValidationError(field) {
      if (!this.validationErrors || !this.validationErrors[field]) {
        return;
      }
      this.$delete(this.validationErrors, field);
    },
    validateOrganizationAndWarehouseRequired() {
      let valid = true;
      const errors = { ...(this.validationErrors || {}) };

      if (!this.document.organization_id) {
        errors.organization_id = [this.$t("GENERAL.VALIDATION.REQUIRED_FIELD")];
        valid = false;
      } else {
        delete errors.organization_id;
      }

      if (!this.document.destination_warehouse_id) {
        errors.destination_warehouse_id = [this.$t("GENERAL.VALIDATION.REQUIRED_FIELD")];
        valid = false;
      } else {
        delete errors.destination_warehouse_id;
      }

      if (this.validationErrors !== undefined) {
        this.validationErrors = errors;
      }

      if (!valid) {
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("STOCK.DOCUMENTS.ORG_AND_WAREHOUSE_REQUIRED_SAVE"),
          color: "error",
        });
      }

      return valid;
    },
    organizationIdsForWarehouseQuery() {
      const docOrgId = this.document.organization_id
        ? Number(this.document.organization_id)
        : null;

      if (docOrgId) {
        return [docOrgId];
      }

      const contextOrgIds = this.stockContextOrganizationIds.map(Number);
      if (contextOrgIds.length > 0) {
        return contextOrgIds;
      }

      return [];
    },
    async loadOrganizations() {
      const contextIds = this.stockContextOrganizationIds.map(Number);
      const params = {};
      if (contextIds.length > 0) {
        params.organization_ids = contextIds;
      }

      const orgs = await this.$store.dispatch(GET_ORGANIZATIONS_OPTIONS, params);
      let list = orgs.data || orgs;

      if (contextIds.length > 0) {
        list = list.filter((org) => contextIds.includes(Number(org.id)));
      }

      this.organizations = list;
    },
    mapPhysicalWarehouseOptions(warehouses) {
      return this.mapWarehouseOptions(warehouses, ["physical"]);
    },
    mapWarehouseOptions(warehouses, types = ["physical"]) {
      return (warehouses || [])
        .filter((warehouse) => types.includes(warehouse.type))
        .map((warehouse) => ({
          ...warehouse,
          label: warehouse.label || this.warehouseOptionLabel(warehouse),
        }));
    },
    async loadWarehouses() {
      const params = {
        organization_ids: this.organizationIdsForWarehouseQuery(),
      };
      const contextWarehouseIds = this.stockContextWarehouseIds.map(Number);
      const selectedWarehouseIds = [
        this.document.destination_warehouse_id,
        this.document.source_warehouse_id,
      ]
        .filter(Boolean)
        .map(Number);
      const warehouseIdsParam = [
        ...new Set([...contextWarehouseIds, ...selectedWarehouseIds]),
      ];
      if (warehouseIdsParam.length > 0) {
        params.warehouse_ids = warehouseIdsParam;
      }

      const data = await this.$store.dispatch(GET_STOCK_WAREHOUSE_OPTIONS, params);
      const warehouses = data.data || data;
      let options = this.mapWarehouseOptions(warehouses, ["physical", "virtual"]);

      if (contextWarehouseIds.length > 0) {
        options = options.filter(
          (warehouse) =>
            contextWarehouseIds.includes(Number(warehouse.id)) ||
            selectedWarehouseIds.includes(Number(warehouse.id)) ||
            warehouse.type === "virtual"
        );
      }

      this.warehouseOptions = options;

      selectedWarehouseIds.forEach((warehouseId) => {
        const selected = warehouses.find(
          (warehouse) => Number(warehouse.id) === Number(warehouseId)
        );
        if (selected && ["physical", "virtual"].includes(selected.type)) {
          this.warehouseOptions = this.mergeOptionIntoList(this.warehouseOptions, {
            ...selected,
            label: selected.label || this.warehouseOptionLabel(selected),
          });
        }
      });

      if (typeof this.reconcileHeaderOptionsFromSeed === "function") {
        this.reconcileHeaderOptionsFromSeed();
      }
    },
    resetDocumentSelectionsOutsideContext() {
      if (this.suppressStockHeaderReload) {
        return;
      }

      const orgId = this.document.organization_id;
      if (
        orgId &&
        !this.organizations.some((org) => Number(org.id) === Number(orgId))
      ) {
        if (!(this.isNew && this.headerOptionsSeed)) {
          this.document.organization_id = null;
          this.document.destination_warehouse_id = null;
          this.document.source_warehouse_id = null;
        }
        return;
      }

      const destinationWarehouseId = this.document.destination_warehouse_id;
      if (
        destinationWarehouseId &&
        !this.warehouseOptions.some(
          (warehouse) => Number(warehouse.id) === Number(destinationWarehouseId)
        )
      ) {
        if (typeof this.reconcileHeaderOptionsFromSeed === "function") {
          this.reconcileHeaderOptionsFromSeed();
        }
        if (
          !this.warehouseOptions.some(
            (warehouse) => Number(warehouse.id) === Number(destinationWarehouseId)
          ) &&
          !(this.isNew && this.headerOptionsSeed)
        ) {
          this.document.destination_warehouse_id = null;
        }
      }

      const sourceWarehouseId = this.document.source_warehouse_id;
      if (
        sourceWarehouseId &&
        !this.warehouseOptions.some(
          (warehouse) => Number(warehouse.id) === Number(sourceWarehouseId)
        )
      ) {
        if (typeof this.reconcileHeaderOptionsFromSeed === "function") {
          this.reconcileHeaderOptionsFromSeed();
        }
        if (
          !this.warehouseOptions.some(
            (warehouse) => Number(warehouse.id) === Number(sourceWarehouseId)
          ) &&
          !(this.isNew && this.headerOptionsSeed)
        ) {
          this.document.source_warehouse_id = null;
        }
      }
    },
    applyStockContextDefaultsOnNewDocument() {
      if (!this.isNew) {
        return;
      }

      if (!this.document.organization_id && this.organizations.length === 1) {
        this.document.organization_id = this.organizations[0].id;
      }

      if (
        !this.document.destination_warehouse_id &&
        this.warehouseOptions.length === 1
      ) {
        this.document.destination_warehouse_id = this.warehouseOptions[0].id;
      }

      this.applyRouteQueryDefaultsOnNewDocument();
    },
    applyRouteQueryDefaultsOnNewDocument() {
      if (!this.isNew || !this.$route || !this.$route.query) {
        return;
      }

      const query = this.$route.query;

      if (this.hasStockDraftSourceQuery()) {
        if (query.direction && Object.prototype.hasOwnProperty.call(this.document, "direction")) {
          this.document.direction = query.direction;
        }
        if (query.incoterm_id && !this.document.incoterm_id) {
          this.document.incoterm_id = Number(query.incoterm_id);
        }
        return;
      }

      if (query.organization_id && !this.document.organization_id) {
        this.document.organization_id = Number(query.organization_id);
      }

      if (
        query.deal_id &&
        !this.document.deal_id &&
        Object.prototype.hasOwnProperty.call(this.document, "deal_id")
      ) {
        this.applyDealIdFromQuery(Number(query.deal_id));
      }

      [
        "supplier_company_id",
        "customer_company_id",
        "source_company_id",
        "destination_company_id",
        "company_id",
      ].forEach((key) => {
        if (
          query[key] &&
          Object.prototype.hasOwnProperty.call(this.document, key) &&
          !this.document[key]
        ) {
          this.document[key] = Number(query[key]);
        }
      });

      if (typeof this.applyDealPartiesToDocument === "function") {
        this.applyDealPartiesToDocument(this.resolveDealForParties?.() || null);
      }

      if (query.direction && Object.prototype.hasOwnProperty.call(this.document, "direction")) {
        this.document.direction = query.direction;
      }

      if (query.incoterm_id && !this.document.incoterm_id) {
        this.document.incoterm_id = Number(query.incoterm_id);
      }
    },
    applyDealIdFromQuery(dealId) {
      if (!dealId) {
        return;
      }

      this.document.deal_id = dealId;

      if (typeof this.seedDealOptionFromId === "function") {
        this.seedDealOptionFromId(dealId);
      }

      if (typeof this.loadDeals === "function") {
        this.loadDeals();
      }
    },
    hasStockDraftSourceQuery() {
      const query = this.$route?.query || {};
      return Boolean(
        query.sales_order_id || query.purchase_order_id || query.transport_order_id
      );
    },
    async handleStockContextChange() {
      if (this.suppressStockHeaderReload) {
        return;
      }

      await this.loadOrganizations();

      if (this.isNew) {
        this.resetDocumentSelectionsOutsideContext();
      }

      await this.loadWarehouses();

      if (this.isNew) {
        this.applyStockContextDefaultsOnNewDocument();
      }

      if (typeof this.loadDeals === "function") {
        await this.loadDeals();
      }

      if (typeof this.reconcileHeaderOptionsFromSeed === "function") {
        this.reconcileHeaderOptionsFromSeed();
      }
    },
  },
};
